import type { TypeContextCourant } from "@/features/auth/context-app"
import {
  type Etat,
  type Transition,
  type TransitionCallback,
  type TransitionCallbackOptions,
  type Transitions,
  TransitionBack,
} from './types'
import { isFunction } from '@/features/shared/tools/function.ts'

export type MachineEtatOptions<TLaunchParams, TResult> = {
  transitions: Transitions<TLaunchParams>
  resultCallback: TransitionCallback<TLaunchParams, TResult>
  disposeCallback?: TransitionCallback<TLaunchParams, void>
}

export class MachineEtat<TLaunchParams = void, TResult = void> {
  #transitions: Transitions<TLaunchParams>
  #resultCallback: TransitionCallback<TLaunchParams, TResult>
  #disposeCallback?: TransitionCallback<TLaunchParams, void>

  constructor(options: MachineEtatOptions<TLaunchParams, TResult>) {
    this.#transitions = options.transitions
    this.#resultCallback = options.resultCallback
    this.#disposeCallback = options.disposeCallback
  }

  private rechercheTransitionSuivante(etatDepart: string | null, options: TransitionCallbackOptions<TLaunchParams>) {
    // console.log(`Recherche :${etatDepart}`, options)
    return this.#transitions.find(item => {
      // console.log(etatDepart, item.etatDepart, item.etatArrive)
      if (Array.isArray(item.etatDepart) && !item.etatDepart.includes(etatDepart)) return false
      if (!Array.isArray(item.etatDepart) && item.etatDepart !== etatDepart) return false
      try {
        return item.if?.call(null, options) ?? true
      } catch (err) {
        console.error(
          `Une erreur est survenue sur la transition ${etatDepart} vers ${item.etatArrive}, exécution du if: ${item.if}.`,
          err
        )
        throw err
      }
    })
  }

  private buildEtatParams(etats: Etat[]) {
    return etats.reduce((acc, curr) => {
      acc[curr.nom] = curr.model
      return acc
    }, {} as Record<string, any>)
  }

  async launch(options: {
    titre: string,
    contextCourant: TypeContextCourant,
    launchParams?: TLaunchParams
  }): Promise<TResult> {
    let etats: Etat[] = []

    const { titre, contextCourant, launchParams } = options

    let transitionSuivante: Transition<any, any, TLaunchParams> | undefined =
      this.rechercheTransitionSuivante(null, { etats: {}, launchParams, contextCourant })

    let goBackTransitionState = undefined
    try {

      while (transitionSuivante) {
        const canGoBack = etats.some(etat => etat.transitionType === "interactif")

        // Obtient In
        const inResult = transitionSuivante.queryIn?.call(
          null, { etats: this.buildEtatParams(etats), launchParams, contextCourant }
        )

        // Execute la transition
        const runnable = isFunction(transitionSuivante.runnable) ?
          transitionSuivante.runnable() :
          transitionSuivante.runnable

        try {
          const result = await runnable.run({ modelIn: inResult, canGoBack, titre: titre, contextCourant, transitionState: goBackTransitionState })
          goBackTransitionState = undefined

          // Affecte nouvel Etat
          const etat: Etat = {
            nom: transitionSuivante.etatArrive,
            model: result.out,
            transitionType: result.type,
            transtionState: result.type === 'interactif' ? result.transitionState : undefined,
          }
          etats.push(etat)

          // Recherche definition suivante
          transitionSuivante = this.rechercheTransitionSuivante(
            etat.nom, { etats: this.buildEtatParams(etats), launchParams, contextCourant }
          )
        } catch (err) {
          if (err instanceof TransitionBack === false) throw err

          // Recherche transition interactive précédente
          let index = etats.length - 1
          while (index > -1) {
            if (etats[index]?.transitionType === 'interactif') {
              goBackTransitionState = etats[index]?.transtionState
              etats = etats.slice(0, index)
              break
            }
            index -= 1
          }
          const nouvelEtat = etats[etats.length - 1]
          transitionSuivante = this.rechercheTransitionSuivante(
            nouvelEtat?.nom ?? null, { etats: this.buildEtatParams(etats), launchParams, contextCourant }
          )
        }

      }
    } finally {
      this.#disposeCallback?.call(null, {
        launchParams,
        etats: this.buildEtatParams(etats),
        contextCourant
      })
    }
    // console.log(this.buildEtatParams(etats))
    return this.#resultCallback({
      launchParams,
      etats: this.buildEtatParams(etats),
      contextCourant
    })
  }

}
